import React, { useState, useEffect, useMemo } from 'react';
import HostNav from '../components/hostComponents/HostNav';
import Property from '../components/hostComponents/Property';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getHostProperties, getHostEarnings, getAllOrders } from '../store/thunks/hostActions';
import { buildPropertyEarningsMap } from '../utils/earningsAggregation';

function PropertiesStatsBar({ properties }) {
  const total = properties?.length || 0;
  const active = properties?.filter(p => !p.isDeleted).length || 0;
  const syncedCount = properties?.filter(
    (property) => property.propertySource && property.propertySource !== 'manual'
  ).length || 0;

  const stats = [
    { label: 'Total Properties', value: total },
    { label: 'Active', value: active },
    { label: 'Synced via PMS', value: syncedCount },
  ];

  return (
    <div className="grid grid-cols-3 gap-2.5 mb-4">
      {stats.map((stat) => (
        <div key={stat.label} className="rounded-[14px] bg-white border border-[#EAEAEA] px-4 py-3.5">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">{stat.label}</p>
          <p className="mt-1 text-[20px] font-extrabold leading-tight tabular-nums text-[#111111]" style={{ letterSpacing: -0.3 }}>
            {stat.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function ViewToggle({ viewMode, setViewMode }) {
  return (
    <div className="flex items-center rounded-[10px] bg-[#F5F5F5] p-0.5">
      <button
        onClick={() => setViewMode('list')}
        title="List view"
        aria-label="List view"
        className={`flex items-center justify-center h-8 w-8 rounded-[8px] transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-[#111111]' : 'text-[#888888] hover:text-[#111111]'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </button>
      <button
        onClick={() => setViewMode('grid')}
        title="Grid view"
        aria-label="Grid view"
        className={`flex items-center justify-center h-8 w-8 rounded-[8px] transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-[#111111]' : 'text-[#888888] hover:text-[#111111]'}`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      </button>
    </div>
  );
}

function copyToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch (e) { /* no-op */ }
  document.body.removeChild(ta);
}

function PropertyListRow({ property, propertyTotals }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const propertyEarnings = propertyTotals?.totalHostFee || 0;
  const orderCount = propertyTotals?.orderCount || 0;
  const formatAddress = (addr) => (!addr ? 'N/A' : [addr.line1, addr.city, addr.state].filter(Boolean).join(', '));

  const handleCopyLink = (e) => {
    e.stopPropagation();
    if (!property.uniqueLink) return;
    copyToClipboard(property.uniqueLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div
      onClick={() => navigate(`/properties/${property._id}`)}
      className="group grid grid-cols-[1fr_180px_70px_90px_24px] sm:grid-cols-[1fr_180px_70px_90px_24px] items-center gap-4 px-4 py-3 bg-white border border-[#EAEAEA] rounded-[14px] hover:border-[#111111] transition-colors cursor-pointer"
    >
      <div className="flex items-center space-x-3 min-w-0">
        <div className="w-9 h-9 bg-[#F5F5F5] rounded-[10px] flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-[#5F6368]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-[#111111] truncate">{property.listingTitle || 'Untitled Property'}</p>
          <p className="text-[11px] text-[#888888] truncate">{formatAddress(property.address)}</p>
        </div>
      </div>
      <div className="flex justify-start">
        {property.uniqueLink ? (
          <button
            type="button"
            onClick={handleCopyLink}
            className="flex items-center justify-center gap-1.5 w-[180px] min-w-[180px] h-8 px-3 rounded-[10px] bg-white hover:bg-[#FAFAFA] text-[#5F6368] hover:text-[#111111] text-[12px] font-bold transition-colors border border-[#EAEAEA] whitespace-nowrap"
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span className="truncate">{copied ? 'Copied!' : 'Unique Shopping Link'}</span>
          </button>
        ) : (
          <span className="w-[180px] min-w-[180px] block" aria-hidden />
        )}
      </div>
      <div className="hidden sm:block text-right">
        <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#888888]">Orders</p>
        <p className="text-[13px] font-extrabold text-[#111111] tabular-nums">{orderCount}</p>
      </div>
      <div className="text-right">
        <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#888888]">Earned</p>
        <p className="text-[13px] font-extrabold text-[#047857] tabular-nums">${propertyEarnings.toFixed(2)}</p>
      </div>
      <svg className="w-4 h-4 text-[#BBBBBB] group-hover:text-[#111111] transition-colors justify-self-end" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
}

const PropertiesScreen = () => {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const { properties, loading: loadingProperties } = useSelector((state) => state.hostProperties);
  const { earnings } = useSelector((state) => state.hostEarnings);
  const { orders: allOrders } = useSelector((state) => state.orders);

  const [viewMode, setViewMode] = useState('list');
  const activePropertiesList = properties?.filter(p => !p.isDeleted) || [];
  const propertyEarningsMap = useMemo(
    () => buildPropertyEarningsMap({ orders: allOrders, earningsByProperty: earnings }),
    [allOrders, earnings]
  );

  const earningsArray = useMemo(() => {
    if (!propertyEarningsMap || typeof propertyEarningsMap !== 'object') return [];
    return Object.keys(propertyEarningsMap).map((propertyId) => ({
      _id: propertyId,
      totalCommission: propertyEarningsMap[propertyId]?.totalHostFee || 0
    }));
  }, [propertyEarningsMap]);

  useEffect(() => {
    if (hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      dispatch(getAllOrders(hostInfo._id));
    }
  }, [hostInfo?._id, dispatch]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto px-6 py-6">
        <HostNav hide={true} />

        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1">Properties</p>
            <h1 className="text-[26px] font-extrabold text-[#111111] leading-tight" style={{ letterSpacing: -0.5 }}>
              Property Management
            </h1>
            <p className="text-[13px] text-[#5F6368] mt-0.5">Manage your properties and PMS integrations</p>
          </div>
          <Link
            to="/host"
            className="inline-flex shrink-0 items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:text-[#111111] hover:border-[#111111]"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Dashboard
          </Link>
        </div>

        <PropertiesStatsBar properties={properties} />

        <div className="flex flex-wrap items-center gap-2 rounded-[14px] bg-white border border-[#EAEAEA] p-3 mb-4">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <div className="flex-1" />
          <Link to="/integrations" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:text-[#111111] hover:border-[#111111]">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Integrations
          </Link>
          <Link to="/add-property" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] bg-[#111111] text-white text-[12px] font-bold transition hover:bg-black">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Property
          </Link>
        </div>

        {loadingProperties ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-[14px] border border-[#EAEAEA] p-4 animate-pulse">
                <div className="h-4 bg-[#F0F0F0] rounded w-3/4 mb-3" />
                <div className="h-3 bg-[#F0F0F0] rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : activePropertiesList.length === 0 ? (
          <div className="rounded-[18px] bg-white border border-[#EAEAEA] px-6 py-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-14 h-14 bg-[#F5F5F5] rounded-[14px] flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-[#5F6368]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <h3 className="text-[16px] font-extrabold text-[#111111] mb-1">No Properties Yet</h3>
              <p className="text-[13px] text-[#888888] mb-5">Add a property or connect Track, Guesty, Streamline, or Hospitable to sync.</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link to="/add-property" className="inline-flex items-center h-9 px-4 rounded-[10px] bg-[#111111] text-white text-[12px] font-bold transition hover:bg-black">
                  Add Property
                </Link>
                <Link to="/integrations" className="inline-flex items-center h-9 px-4 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:text-[#111111] hover:border-[#111111]">
                  Integrations
                </Link>
              </div>
            </div>
          </div>
        ) : viewMode === 'list' ? (
          <div className="space-y-2">
            {activePropertiesList.map((property) => (
              <PropertyListRow key={property._id} property={property} propertyTotals={propertyEarningsMap?.[property._id]} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {activePropertiesList.map((property) => (
              <Property key={property._id} property={property} data={earningsArray} rawDataList={[]} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesScreen;
